import * as React from 'react';

import { Button, TextField, ReMatterLogo, cn } from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import { useLocation } from 'wouter';

import { useAcceptCustomerInvitationMutation } from '~/modules/crm/_graphql/mutationCustomerInvitationAccept';
import { useCustomerInvitationByTokenQuery } from '~/modules/crm/_graphql/queryCustomerInvitationByToken';
import { useTranslations } from '~/shared/i18n/useTranslations';

const containerVariants = cva([
  'relative flex min-h-screen items-center justify-center overflow-hidden p-5',
  'bg-[radial-gradient(circle_at_20%_30%,rgba(99,102,241,0.03)_0%,transparent_50%),radial-gradient(circle_at_80%_70%,rgba(99,102,241,0.03)_0%,transparent_50%),linear-gradient(45deg,rgba(99,102,241,0.01)_25%,transparent_25%,transparent_75%,rgba(99,102,241,0.01)_75%),linear-gradient(-45deg,rgba(99,102,241,0.01)_25%,transparent_25%,transparent_75%,rgba(99,102,241,0.01)_75%),linear-gradient(120deg,#f5f7ff_0%,#ffffff_100%)]',
  'bg-[length:100%_100%,100%_100%,60px_60px,60px_60px,100%_100%]',
]);

const cardVariants = cva([
  'relative z-10 flex w-full max-w-[400px] flex-col rounded-xl p-10 shadow-lg',
  'border border-white/20 bg-white/80 backdrop-blur-sm',
]);

type UserRegistrationForm = {
  firstName: string;
  lastName: string;
  password: string;
};

export const CustomerInvitationAccept = () => {
  const t = useTranslations();
  const [, setLocation] = useLocation();
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState<UserRegistrationForm>({
    firstName: '',
    lastName: '',
    password: '',
  });

  const { mutateAsync: acceptInvitation, isPending: isAcceptingInvitation } =
    useAcceptCustomerInvitationMutation();

  // Get token from URL
  const token = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }, []);

  // Use query hook to load invitation
  const {
    data: queryData,
    error: queryError,
    isLoading: isLoadingInvitation,
  } = useCustomerInvitationByTokenQuery({ token: token || '' });

  const invitation = queryData?.customerInvitationByToken;

  // Handle query results
  React.useEffect(() => {
    if (!token) {
      setError(t.auth.customerInvitation.invalidLink);
      return;
    }

    if (queryError) {
      console.error('Error loading invitation:', queryError);
      setError(t.auth.customerInvitation.loadError);
      return;
    }

    if (invitation) {
      const expiresAt = new Date(invitation.expiresAt);
      const now = new Date();

      if (invitation.invitationStatus === 'EXPIRED' || expiresAt < now) {
        setError(t.auth.customerInvitation.expiredInvitation);
        return;
      }

      if (invitation.invitationStatus === 'ACCEPTED') {
        setSuccess(t.auth.customerInvitation.alreadyAccepted);
        return;
      }

      if (invitation.invitationStatus === 'CANCELLED') {
        setError(t.auth.customerInvitation.cancelledInvitation);
        return;
      }

      setError(null);
    } else if (queryData && !invitation) {
      setError(t.auth.customerInvitation.invalidToken);
    }
  }, [token, queryError, invitation, queryData, t]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !invitation) return;

    try {
      setError(null);

      const result = await acceptInvitation({
        token,
        userData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
        },
      });

      if (result.crmCustomerInvitationAccept.success) {
        setSuccess(
          t.auth.customerInvitation.welcomeMessage(
            result.crmCustomerInvitationAccept.customerOrganizationName,
          ),
        );

        setTimeout(() => {
          setLocation('/sign-in');
        }, 3000);
      } else {
        setError(t.auth.customerInvitation.failedToAccept);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(t.auth.customerInvitation.processingError);
    }
  };

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.password.trim() !== '';
  const isLoading = isLoadingInvitation || isAcceptingInvitation;

  const renderContent = () => {
    if (isLoading && !invitation) {
      return (
        <div className="flex flex-col items-center space-y-4">
          <div className="size-8 animate-spin rounded-full border-4 border-neutral/30 border-t-primary"></div>
          <p className="text-sm text-neutral">
            {t.auth.customerInvitation.loadingInvitation}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col space-y-4">
          <div className="rounded-lg bg-warning/10 p-4">
            <h3 className="text-sm font-medium text-warning">
              {t.auth.customerInvitation.invitationError}
            </h3>
            <p className="mt-1 text-sm text-warning/80">{error}</p>
          </div>
          <Button
            variant="outline"
            onPress={() => setLocation('/sign-in')}
            className="w-full"
          >
            {t.auth.customerInvitation.goToSignIn}
          </Button>
        </div>
      );
    }

    if (success) {
      return (
        <div className="flex flex-col space-y-4">
          <div className="rounded-lg bg-success/10 p-4">
            <h3 className="text-sm font-medium text-success">
              {t.auth.customerInvitation.success}
            </h3>
            <p className="mt-1 text-sm text-success/80">{success}</p>
          </div>
          <div className="flex items-center justify-center">
            <div className="size-4 animate-spin rounded-full border-2 border-success border-t-transparent"></div>
            <span className="ml-2 text-sm text-neutral">
              {t.auth.customerInvitation.redirecting}
            </span>
          </div>
        </div>
      );
    }

    if (!invitation) {
      return null;
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-neutral/80">
            {t.auth.customerInvitation.title}
          </h1>
          <p className="mt-2 text-sm text-neutral">
            {t.auth.customerInvitation.subtitle(invitation.account.name)}
          </p>
        </div>

        <form onSubmit={onSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TextField
                  label={t.auth.customerInvitation.firstName}
                  placeholder={t.auth.customerInvitation.firstNamePlaceholder}
                  value={formData.firstName}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, firstName: value }))
                  }
                  isDisabled={isLoading}
                  isRequired
                />
              </div>
              <div>
                <TextField
                  label={t.auth.customerInvitation.lastName}
                  placeholder={t.auth.customerInvitation.lastNamePlaceholder}
                  value={formData.lastName}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, lastName: value }))
                  }
                  isDisabled={isLoading}
                  isRequired
                />
              </div>
            </div>

            <TextField
              type="password"
              label={t.auth.customerInvitation.passwordLabel}
              placeholder={t.auth.customerInvitation.passwordPlaceholder}
              value={formData.password}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, password: value }))
              }
              isDisabled={isLoading}
              isRequired
            />

            <Button
              type="submit"
              isDisabled={!isFormValid || isLoading}
              className="w-full"
            >
              {isLoading
                ? t.auth.customerInvitation.processing
                : t.auth.customerInvitation.submitButton}
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-neutral/60">
          {t.auth.customerInvitation.disclaimer}
        </p>
      </div>
    );
  };

  return (
    <div className={cn('@CustomerInvitationAccept', containerVariants())}>
      <div className={cardVariants()}>
        <div className="mb-6 flex justify-center">
          <ReMatterLogo size="large" height={50} />
        </div>
        {renderContent()}
      </div>
    </div>
  );
};
